// Copyright (c) 2025 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  ShortTextRounded,
  SubjectRounded,
} from '@/constants/hardfin-standard-icons';
import { ItemFieldType } from '@/items/constants/item-field-types';
import {
  Box,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  styled,
  toggleButtonClasses,
} from '@mui/material';

/**
 * Styled toggle button group for field format selection.
 */
const FormatToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: theme.spacing(2),
  width: '100%',
  [`& .${toggleButtonClasses.root}`]: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(2),
    textTransform: 'none',
    flexDirection: 'column',
    alignItems: 'flex-start',
    transition: theme.transitions.create(['border-color', 'background-color']),
  },
  [`& .${toggleButtonClasses.selected}`]: {
    borderColor: theme.palette.primary.main,
    backgroundColor: `${theme.palette.primary.main}08`,
    '&:hover': {
      backgroundColor: `${theme.palette.primary.main}12`,
    },
  },
}));

interface FormatOption {
  value: ItemFieldType;
  label: string;
  description: string;
  icon: React.ReactNode;
  example: React.ReactNode;
}

const TEXT_FORMAT_OPTIONS: FormatOption[] = [
  {
    value: 'text',
    label: 'Single line',
    description: 'Best for short text like names, IDs, or brief descriptions',
    icon: <ShortTextRounded color="primary" />,
    example: (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1,
          mt: 1,
          height: 32,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Single line of text...
        </Typography>
      </Paper>
    ),
  },
  {
    value: 'multiline_text',
    label: 'Multiple lines',
    description: 'Best for longer text like comments, notes, or paragraphs',
    icon: <SubjectRounded color="primary" />,
    example: (
      <Paper
        variant="outlined"
        sx={{
          width: '100%',
          p: 1,
          mt: 1,
          height: 68,
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" align="left">
          Multiple lines of text,
          <br />
          including newlines
        </Typography>
      </Paper>
    ),
  },
];

interface Props {
  value: ItemFieldType;
  onChange: (value: ItemFieldType) => void;
}

/**
 * A component that allows users to select between different text format options.
 */
export function ItemFieldFormatSelector(props: Props) {
  const handleChange = (_: unknown, value: ItemFieldType | null) => {
    if (value === null) {
      return;
    }
    props.onChange(value);
  };

  return (
    <FormatToggleButtonGroup
      exclusive
      value={props.value}
      onChange={handleChange}
    >
      {TEXT_FORMAT_OPTIONS.map((option) => (
        <ToggleButton key={option.value} value={option.value}>
          <Stack spacing={1} alignItems="flex-start" width="100%" height="100%">
            <Stack spacing={1} width="100%">
              <Box display="flex" alignItems="center" gap={1}>
                {option.icon}
                <Typography variant="subtitle1">{option.label}</Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {option.description}
              </Typography>
            </Stack>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              width="100%"
              flex={1}
              minHeight={80}
            >
              {option.example}
            </Box>
          </Stack>
        </ToggleButton>
      ))}
    </FormatToggleButtonGroup>
  );
}
